import { useEffect, useMemo, useRef } from "react";
import { AlertCircle, AlertTriangle, Loader2, Sparkles, Square } from "lucide-react";
import type { UseQueryResult } from "@tanstack/react-query";
import type {
  AssistantCommandView,
  AssistantStreamEvent,
  ConversationDetail,
  DiscardedCommandReason,
  DiscardedCommandView,
} from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Notify } from "@/components/ui/notify";
import type { StreamPhase } from "@/types/assistant.types";
import { MessageBubble } from "./MessageBubble";
import { CommandCard } from "./CommandCard";
import { ACTION_LABEL } from "./CommandCard/action-labels";

// Motivo (amigável) de um comando proposto pela engine e DESCARTADO na validação da plataforma —
// exibido no dock p/ o usuário entender por que "o assistente disse que fez e nada aconteceu".
const DISCARD_REASON: Record<DiscardedCommandReason, string> = {
  not_in_catalog: "ação não reconhecida",
  invalid_args: "dados incompletos ou inválidos",
  forbidden_role: "requer perfil GESTOR",
  cap_exceeded: "limite de ações por resposta",
};

interface ThreadViewProps {
  conversation: UseQueryResult<ConversationDetail>;
  events: AssistantStreamEvent[];
  phase: StreamPhase;
  turnError: string | null;
  role: "GESTOR" | "OPERADOR";
  onCancel: () => void;
  onExample: (text: string) => void;
}

// Prompts de exemplo por papel (vendem o próximo passo no empty-state).
const EXAMPLES: Record<"GESTOR" | "OPERADOR", string[]> = {
  OPERADOR: [
    "Como está o OEE desta linha?",
    "Mostre a produção ativa aqui",
    "Quais alarmes estão ativos?",
  ],
  GESTOR: [
    "Como está o OEE desta linha?",
    "Rode a simulação do meu cenário",
    "Sugira melhorias para esta linha",
  ],
};

export function ThreadView({
  conversation,
  events,
  phase,
  turnError,
  role,
  onCancel,
  onExample,
}: ThreadViewProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const messages = conversation.data?.messages ?? [];
  const commands = conversation.data?.commands;

  const byTurn = useMemo(() => {
    const map = new Map<string, AssistantCommandView[]>();
    for (const c of commands ?? []) map.set(c.turnId, [...(map.get(c.turnId) ?? []), c]);
    return map;
  }, [commands]);

  // Comandos descartados por turno (PERSISTIDO na ConversationDetail) — avisamos em vez de deixar o
  // silêncio enganar ("disse que fez, nada aconteceu"). Vem do banco ⇒ sobrevive ao F5, e renderiza
  // por turno como os cards de comando (não do SSE transitório).
  const discardedByTurn = useMemo(() => {
    const map = new Map<string, DiscardedCommandView[]>();
    for (const d of conversation.data?.discarded ?? [])
      map.set(d.turnId, [...(map.get(d.turnId) ?? []), d]);
    return map;
  }, [conversation.data?.discarded]);

  const conversationId = conversation.data?.id ?? null;
  const liveTools = events.filter((e) => e.type === "tool");
  const liveCommands = events.filter(
    (e): e is Extract<AssistantStreamEvent, { type: "command" }> => e.type === "command"
  );
  const hasContent = messages.length > 0 || events.length > 0 || !!turnError;

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages.length, events.length, turnError]);

  if (conversation.isError)
    return (
      <div className="flex-1 overflow-y-auto p-3">
        <Notify.QueryError error={conversation.error} onRetry={() => void conversation.refetch()} />
      </div>
    );

  if (!hasContent && !conversation.isLoading)
    return (
      <div className="flex flex-1 flex-col justify-center gap-4 overflow-y-auto p-4">
        <Notify.Empty
          title="Pergunte sobre a operação"
          description="O assistente lê os dados da plataforma e responde no contexto da tela atual."
          icon={<Sparkles className="h-6 w-6" />}
        />
        <div className="flex flex-col gap-2">
          {EXAMPLES[role].map((ex) => (
            <button
              key={ex}
              type="button"
              onClick={() => onExample(ex)}
              className="rounded-md border border-border bg-background px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted"
            >
              {ex}
            </button>
          ))}
        </div>
      </div>
    );

  return (
    <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-3">
      {messages.map((m) => {
        const turnDiscarded =
          m.role === "ASSISTANT" && m.turnId ? discardedByTurn.get(m.turnId) : null;
        return (
          <div key={m.id} className="space-y-2">
            <MessageBubble role={m.role} content={m.content} />
            {m.role === "ASSISTANT" && m.turnId
              ? (byTurn.get(m.turnId) ?? []).map((c) => (
                  <CommandCard key={c.id} command={c} conversationId={conversationId} />
                ))
              : null}
            {turnDiscarded && turnDiscarded.length > 0 ? (
              <div className="border-[var(--rd-warning-500)]/40 flex items-start gap-2 rounded-md border bg-[var(--rd-warning-50)] px-3 py-2 text-sm text-warning-foreground">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <div>
                  <p className="font-medium">Algumas ações não foram executadas:</p>
                  <ul className="mt-0.5 list-inside list-disc">
                    {turnDiscarded.map((d, i) => (
                      <li key={`${d.name}-${i}`}>
                        {ACTION_LABEL[d.name] ?? d.name} — {DISCARD_REASON[d.reason]}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : null}
          </div>
        );
      })}

      {phase === "streaming" ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 motion-safe:animate-spin" aria-hidden="true" />
            <span>{liveTools.length ? liveTools[liveTools.length - 1]!.step : "Pensando…"}</span>
          </div>
          {liveCommands.map((e) => (
            <CommandCard
              key={e.command.id}
              command={e.command}
              conversationId={conversationId}
              live
            />
          ))}
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 text-muted-foreground"
            onClick={onCancel}
          >
            <Square className="h-3.5 w-3.5" aria-hidden="true" />
            Parar
          </Button>
        </div>
      ) : null}

      {turnError ? (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{turnError}</span>
        </div>
      ) : null}
    </div>
  );
}
